/**
 * Shared MapLibre style helpers for the OSM (transit-maps) tile stack served
 * via the Caddy `/osm/*` proxy.
 *
 * These were originally inlined in `components/maps/FullMap.tsx`. They moved
 * here in PR 4 of the Alert Detail redesign so the new lightweight
 * `AlertMapEmbed` component can share the same style logic without forcing a
 * second copy to drift over time.
 *
 * Two distinct rewrites live here:
 *
 *   1. `rewriteOsmUrl` / `transformRequest` — runs per-request as MapLibre
 *      fetches every tile/sprite/glyph/style. Rewrites bare `/tiles/...`,
 *      `/fonts/...` and `/styles/...` paths to `/osm/...` so they hit Caddy.
 *
 *   2. `transformStyle` (alias of `stripMissingGeoJsonSources`) — runs once
 *      when the style JSON is loaded. It (a) strips decorative GeoJSON
 *      sources the VPS doesn't host (oceans, lakes, …), (b) absolutises the
 *      `sprite` and `glyphs` URLs (MapLibre v5 rejects relative ones), and
 *      (c) rewrites `tiles[]` arrays in vector/raster sources to absolute
 *      `/osm`-prefixed URLs (MapLibre v5 silently drops sources whose tile
 *      URLs are relative — `transformRequest` can't help because the source
 *      is rejected before any request is built).
 *
 * NOTE: The default style URL is still environment-coupled (relative path
 * served by the same origin via Caddy). If we ever split the SPA off the
 * `primacyhelp.cloud` origin we should move this into `VITE_OSM_STYLE_URL`.
 */

import type { RequestParameters, StyleSpecification, SourceSpecification } from 'maplibre-gl';

export const OSM_STYLE_URL = '/osm/styles/osm-bright/style.json';

export function rewriteOsmUrl(rawUrl: string): string {
  try {
    if (rawUrl.startsWith('/osm/')) return rawUrl;
    if (rawUrl.startsWith('http')) {
      const u = new URL(rawUrl);
      if (u.pathname.startsWith('/osm/')) return rawUrl;
      if (u.pathname.startsWith('/tiles/')) return `/osm${u.pathname}${u.search}`;
      if (u.pathname.startsWith('/fonts/')) return `/osm${u.pathname}${u.search}`;
      if (u.pathname.startsWith('/styles/')) return `/osm${u.pathname}${u.search}`;
      return rawUrl;
    }
    if (rawUrl.startsWith('/tiles/')) return `/osm${rawUrl}`;
    if (rawUrl.startsWith('/fonts/')) return `/osm${rawUrl}`;
    if (rawUrl.startsWith('/styles/')) return `/osm${rawUrl}`;
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

export const transformRequest = (url: string, _resourceType?: string): RequestParameters => {
  return { url: rewriteOsmUrl(url) };
};

/**
 * MapLibre `setStyle({ transformStyle })` callback. Drops decorative
 * GeoJSON layers the VPS doesn't ship, and rewrites sprite / glyphs / tile
 * URLs to absolute `/osm`-prefixed forms.
 */
export function transformStyle(
  _previous: StyleSpecification | undefined,
  next: StyleSpecification,
): StyleSpecification {
  if (!next) return next;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const out: StyleSpecification = { ...next };

  if (typeof out.sprite === 'string' && out.sprite.startsWith('/')) {
    out.sprite = `${origin}${out.sprite.startsWith('/osm/') ? '' : '/osm'}${out.sprite}`;
  }
  if (typeof out.glyphs === 'string' && out.glyphs.startsWith('/')) {
    out.glyphs = `${origin}${out.glyphs.startsWith('/osm/') ? '' : '/osm'}${out.glyphs}`;
  }

  if (out.sources) {
    const sources: Record<string, SourceSpecification> = { ...out.sources };
    const dropped = new Set<string>();
    for (const [id, src] of Object.entries(sources)) {
      if (
        src.type === 'geojson' &&
        typeof (src as { data?: unknown }).data === 'string' &&
        ((src as { data: string }).data.startsWith('/data/') ||
          (src as { data: string }).data.includes('/data/'))
      ) {
        delete sources[id];
        dropped.add(id);
      } else if (
        (src.type === 'vector' || src.type === 'raster') &&
        Array.isArray((src as { tiles?: unknown }).tiles)
      ) {
        const fixed = ((src as { tiles: string[] }).tiles).map((t) => {
          if (!t.startsWith('/')) return t;
          if (t.startsWith('/osm/')) return `${origin}${t}`;
          return `${origin}/osm${t}`;
        });
        sources[id] = { ...src, tiles: fixed } as SourceSpecification;
      }
    }
    if (dropped.size > 0) {
      out.layers = (out.layers ?? []).filter((l) => {
        const sourceId = (l as { source?: string }).source;
        return !sourceId || !dropped.has(sourceId);
      });
    }
    out.sources = sources;
  }

  return out;
}
